var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../../../components/ButtonComponent';
import '../../../styles/widgets/clock/clockmanipulative.scss';
import InputBox from '../../../components/InputBox';
import globalStore from '../../../store';
import { announce } from '../../../helper/announcer';
// Convert 12-hour format to 24-hour format
var convert12To24 = function (hour, period) {
    if (period === 'AM') {
        return hour === 12 ? 0 : hour;
    }
    else {
        return hour === 12 ? 12 : hour + 12;
    }
};
// Convert 24-hour format to 12-hour format
var convert24To12 = function (hour24) {
    if (hour24 === 0) {
        return { hour: 12, period: 'AM' };
    }
    else if (hour24 < 12) {
        return { hour: hour24, period: 'AM' };
    }
    else if (hour24 === 12) {
        return { hour: 12, period: 'PM' };
    }
    else {
        return { hour: hour24 - 12, period: 'PM' };
    }
};
// Helper to convert time back to angles
var calculateAnglesFromTime = function (hour, minute) {
    // Minute Hand Angle: 6 degrees per minute
    // 12 o'clock (0 minutes) is 0 degrees.
    var minuteAngle = minute * 6;
    // Hour Hand Angle: 30 degrees per hour + (minute / 60) * 30 degrees
    // We use hour_12 here (1-12)
    var hour_12 = hour === 12 ? 0 : hour; // Treat 12 as 0 for calculation
    var hourAngle = hour_12 * 30 + (minute / 60) * 30;
    // Both hands' angles are calculated based on the 12 o'clock position (0 degrees)
    return { hourAngle: hourAngle, minuteAngle: minuteAngle };
};
var DigitalClock = function (_a) {
    var _clockData = _a._clockData, onClockDataChange = _a.onClockDataChange, movement = _a.movement;
    var _b = __assign({}, _clockData), period = _b.period, hour = _b.hour, minute = _b.minute, initialTimeFormat = _b.timeFormat;
    var t = useTranslation().t;
    var _c = useState('displayTime'), time = _c[0], manipulateTime = _c[1];
    var format = function (num) { return String(num).padStart(2, '0'); };
    var btnConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).btnConfig;
    // Use props for display state
    var _d = useState(hour), hours = _d[0], setHours = _d[1];
    var _e = useState(minute), minutes = _e[0], setMinutes = _e[1];
    var _f = useState(period || 'AM'), ampm = _f[0], setAmPm = _f[1];
    var _g = useState(initialTimeFormat || '12h'), timeFormat = _g[0], setTimeFormat = _g[1];
    var pickerRef = React.useRef(null);
    var setTimeBtnRef = React.useRef(null);
    // Get display hour based on format
    var getDisplayHour = function () {
        if (timeFormat === '24h') {
            return convert12To24(hours, ampm);
        }
        return hours;
    };
    // Toggle time format
    var toggleTimeFormat = function () {
        var newFormat = timeFormat === '12h' ? '24h' : '12h';
        setTimeFormat(newFormat);
        // Notify parent of format change
        var _a = calculateAnglesFromTime(hours, minutes), hourAngle = _a.hourAngle, minuteAngle = _a.minuteAngle;
        onClockDataChange({
            hour: hours,
            minute: minutes,
            period: ampm,
            hourAngle: hourAngle,
            minuteAngle: minuteAngle,
            timeFormat: newFormat,
        });
        announce(t(newFormat === '12h' ? 'text.currentFormat12h' : 'text.currentFormat24h'));
    };
    var handleOnChange = function (newHour, newMinute, newAmPm) {
        setHours(newHour);
        setMinutes(newMinute);
        setAmPm(newAmPm);
        var _a = calculateAnglesFromTime(newHour, newMinute), hourAngle = _a.hourAngle, minuteAngle = _a.minuteAngle;
        onClockDataChange({
            hour: newHour,
            minute: newMinute,
            period: newAmPm,
            hourAngle: hourAngle,
            minuteAngle: minuteAngle,
            timeFormat: timeFormat,
        });
        // Announce time change for screen readers
        var displayHour = timeFormat === '24h' ? convert12To24(newHour, newAmPm) : newHour;
        var periodText = timeFormat === '12h' ? " ".concat(newAmPm) : '';
        announce(t('text.timeSetTo', { time: "".concat(format(displayHour), ":").concat(format(newMinute)).concat(periodText) }));
    };
    // Handle hour change from input in 24h format
    var handleHourInput = function (inputHour) {
        if (timeFormat === '24h') {
            // Convert 24h input to 12h internal format
            var _a = convert24To12(inputHour), hour12 = _a.hour, newPeriod = _a.period;
            handleOnChange(hour12, minutes, newPeriod);
        }
        else {
            handleOnChange(inputHour, minutes, ampm);
        }
    };
    var increase = function (type) {
        var newHour = hours;
        var newMinute = minutes;
        var newAmPm = ampm;
        if (type === 'hours') {
            if (timeFormat === '24h') {
                // In 24h mode, cycle 0-23
                var hour24 = convert12To24(hours, ampm);
                var newHour24 = (hour24 + 1) % 24;
                var converted = convert24To12(newHour24);
                newHour = converted.hour;
                newAmPm = converted.period;
            }
            else {
                // In 12h mode, cycle 1-12
                newHour = (hours % 12) + 1;
                // Toggle AM/PM when crossing from 11 to 12
                if (hours === 11 && newHour === 12) {
                    newAmPm = ampm === 'AM' ? 'PM' : 'AM';
                }
            }
        }
        if (type === 'minutes') {
            newMinute = (minutes + 1) % 60;
            // Carry hour over if minute hits 0 (passes 59) - only for geared movement
            if (newMinute === 0 && movement === 'geared') {
                if (timeFormat === '24h') {
                    var hour24 = convert12To24(hours, ampm);
                    var newHour24 = (hour24 + 1) % 24;
                    var converted = convert24To12(newHour24);
                    newHour = converted.hour;
                    newAmPm = converted.period;
                }
                else {
                    newHour = (hours % 12) + 1;
                    if (newHour === 12) {
                        newAmPm = ampm === 'AM' ? 'PM' : 'AM';
                    }
                }
            }
        }
        if (type === 'ampm') {
            newAmPm = ampm === 'AM' ? 'PM' : 'AM';
        }
        handleOnChange(newHour, newMinute, newAmPm);
    };
    var decrease = function (type) {
        var newHour = hours;
        var newMinute = minutes;
        var newAmPm = ampm;
        if (type === 'hours') {
            if (timeFormat === '24h') {
                // In 24h mode, cycle 0-23
                var hour24 = convert12To24(hours, ampm);
                var newHour24 = (hour24 - 1 + 24) % 24;
                var converted = convert24To12(newHour24);
                newHour = converted.hour;
                newAmPm = converted.period;
            }
            else {
                // In 12h mode, cycle 1-12
                newHour = hours === 1 ? 12 : hours - 1;
                // Toggle AM/PM when crossing from 12 to 11
                if (hours === 12 && newHour === 11) {
                    newAmPm = ampm === 'AM' ? 'PM' : 'AM';
                }
            }
        }
        if (type === 'minutes') {
            newMinute = (minutes - 1 + 60) % 60;
            // Borrow hour if minute hits 59 (passes 0) - only for geared movement
            if (newMinute === 59 && movement === 'geared') {
                if (timeFormat === '24h') {
                    var hour24 = convert12To24(hours, ampm);
                    var newHour24 = (hour24 - 1 + 24) % 24;
                    var converted = convert24To12(newHour24);
                    newHour = converted.hour;
                    newAmPm = converted.period;
                }
                else {
                    newHour = hours === 1 ? 12 : hours - 1;
                    if (newHour === 11) {
                        // Transition from 12:00 to 11:59
                        newAmPm = ampm === 'AM' ? 'PM' : 'AM';
                    }
                }
            }
        }
        if (type === 'ampm') {
            newAmPm = ampm === 'AM' ? 'PM' : 'AM';
        }
        handleOnChange(newHour, newMinute, newAmPm);
    };
    // Sync local state with incoming props
    useEffect(function () {
        setHours(hour);
    }, [hour]);
    useEffect(function () {
        setMinutes(minute);
    }, [minute]);
    useEffect(function () {
        setAmPm(period);
    }, [period]);
    useEffect(function () {
        // Always sync timeFormat from props (for undo/redo support)
        setTimeFormat(initialTimeFormat || '12h');
    }, [initialTimeFormat]);
    var handleSetTime = function () {
        // Blur the currently focused input to trigger its onChange handler
        // This ensures typed values are saved before closing the picker
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        manipulateTime('displayTime');
        // Restore focus to Set Time button
        setTimeout(function () { var _a; return (_a = setTimeBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 50);
    };
    // Focus trap & keyboard handling for picker
    var handlePickerKeyDown = function (e) {
        var _a;
        // Stop arrow keys from rotating clock hands
        if (e.key.startsWith('Arrow')) {
            e.stopPropagation();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            handleSetTime();
            return;
        }
        if (e.key === 'Tab') {
            var focusable = (_a = pickerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), input:not([disabled])');
            if (!focusable || focusable.length === 0)
                return;
            var first = focusable[0];
            var last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    };
    // Auto-focus first button when picker opens
    useEffect(function () {
        if (time === 'setTime') {
            setTimeout(function () {
                var _a;
                var first = (_a = pickerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button:not([disabled]):not([tabindex="-1"]), input:not([disabled])');
                first === null || first === void 0 ? void 0 : first.focus();
            }, 50);
        }
    }, [time]);
    useEffect(function () {
        if (time !== 'setTime')
            return;
        var handleClickOutside = function (e) {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                handleSetTime();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return function () {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [time, hours, minutes, ampm]);
    return (_jsx(_Fragment, { children: time === 'displayTime' ? (_jsx(React.Fragment, { children: _jsxs("div", { className: "digital_clock_parent flexItem", children: [_jsx("div", { className: "digital_clock_displayer flexItem", children: _jsxs("div", { className: "digital_time", "aria-label": t('text.currentTimeLabel', {
                                time: "".concat(format(getDisplayHour()), ":").concat(format(minutes)),
                                period: timeFormat === '12h' ? ampm : '',
                            }), children: [_jsxs("div", { className: "digital_hour", children: [_jsx("span", { children: format(getDisplayHour())[0] }), _jsx("span", { children: format(getDisplayHour())[1] })] }), _jsx("span", { children: ":" }), _jsxs("div", { className: "digital_minute", children: [_jsx("span", { children: format(minutes)[0] }), _jsx("span", { children: format(minutes)[1] })] }), timeFormat === '12h' && _jsx("div", { className: "digital_period", children: ampm })] }) }), _jsx(ButtonComponent, { reference: setTimeBtnRef, defaultCSS: "setTime", normalCSS: "", ariaLabel: "text.setTime", clickHandler: function () { return manipulateTime(function () { return 'setTime'; }); } })] }) })) : (_jsx("div", { className: "timepicker-wrapper", ref: pickerRef, onKeyDown: handlePickerKeyDown, role: "dialog", "aria-modal": "true", "aria-label": t('text.timePickerDialog'), children: _jsxs("div", { className: "time-box", children: [_jsxs("div", { className: "col-holder", children: [_jsxs("div", { className: "col", children: [_jsx("div", { className: "arrowDiv btnRightBorder", children: _jsx(ButtonComponent, { imgWidth: 15, imgSrc: btnConfig.upArrow.imgSrc, clickHandler: function () { return increase('hours'); }, defaultCSS: "arrow up", ariaLabel: "text.increaseHours", ariaControls: "hoursInput" }) }), _jsx(InputBox, { id: 'hoursInput', defaultCSS: "value flexItem", isNumeric: true, type: "text", value: format(getDisplayHour()), maxLength: 2, min: timeFormat === '24h' ? 0 : 1, max: timeFormat === '24h' ? 23 : 12, ariaLabel: t('text.hoursInputWithRange', {
                                            min: timeFormat === '24h' ? 0 : 1,
                                            max: timeFormat === '24h' ? 23 : 12,
                                        }), 
                                        // onInput={(_id, data) => {
                                        //   setHours(+data);
                                        // }}
                                        onChange: function (_id, event, data) {
                                            if (event === 'blur') {
                                                handleHourInput(Number(data));
                                            }
                                        } }), _jsx("div", { className: "arrowDiv btnRightBorder", children: _jsx(ButtonComponent, { imgWidth: 15, imgSrc: btnConfig.downArrow.imgSrc, clickHandler: function () { return decrease('hours'); }, defaultCSS: "arrow down ", ariaLabel: "text.decreaseHours", ariaControls: "hoursInput" }) })] }), _jsxs("div", { className: "col", children: [_jsx("div", { className: "arrowDiv btnRightBorder", children: _jsx(ButtonComponent, { imgWidth: 15, imgSrc: btnConfig.upArrow.imgSrc, clickHandler: function () { return increase('minutes'); }, defaultCSS: "arrow up", ariaLabel: "text.increaseMinutes", ariaControls: "minutesInput" }) }), _jsx(InputBox, { id: 'minutesInput', defaultCSS: "value flexItem", isNumeric: true, type: "text", value: format(minutes), maxLength: 2, min: 0, max: 59, ariaLabel: t('text.minutesInput'), 
                                        // onInput={(_id, data) => {
                                        //   setMinutes(+data);
                                        // }}
                                        onChange: function (_id, event, data) {
                                            if (event === 'blur') {
                                                handleOnChange(hours, Number(data), ampm);
                                            }
                                        } }), _jsx("div", { className: "arrowDiv btnRightBorder", children: _jsx(ButtonComponent, { imgWidth: 15, imgSrc: btnConfig.downArrow.imgSrc, clickHandler: function () { return decrease('minutes'); }, defaultCSS: "arrow down", ariaLabel: "text.decreaseMinutes", ariaControls: "minutesInput" }) })] }), _jsxs("div", { className: "col", style: { paddingRight: 0 }, children: [_jsx("div", { className: "arrowDiv", children: _jsx(ButtonComponent, { imgWidth: 15, imgSrc: btnConfig.upArrow.imgSrc, clickHandler: function () { return increase('ampm'); }, defaultCSS: "arrow up", normalCSS: "arrowNormal", ariaLabel: "text.increasePeriod", disabled: timeFormat !== '12h' }) }), _jsx("div", { className: "value flexItem ampm ".concat(timeFormat !== '12h' ? 'disableAmPm' : ''), "aria-live": "polite", "aria-atomic": "true", "aria-label": t('text.currentPeriod', { period: ampm }), "aria-hidden": timeFormat !== '12h', children: ampm }), _jsx("div", { className: "arrowDiv", children: _jsx(ButtonComponent, { imgWidth: 15, imgSrc: btnConfig.downArrow.imgSrc, clickHandler: function () { return decrease('ampm'); }, defaultCSS: "arrow down", normalCSS: "arrowNormal", ariaLabel: "text.decreasePeriod", disabled: timeFormat !== '12h' }) })] })] }), _jsx("hr", { className: "horizontalLine", "aria-hidden": "true" }), _jsxs("div", { className: "formatSetter", children: [_jsx("div", { className: "arrowDiv btnRightBorder formatSetterArrowWidth", children: _jsx(ButtonComponent, { imgWidth: 12.5, imgSrc: btnConfig.leftArrow.imgSrc, clickHandler: function () { return toggleTimeFormat(); }, defaultCSS: "arrow left", ariaLabel: timeFormat === '12h' ? 'text.switchTo24h' : 'text.switchTo12h' }) }), _jsx("div", { className: "timeFormat", "aria-live": "polite", "aria-atomic": "true", "aria-label": timeFormat === '12h' ? t('text.currentFormat12h') : t('text.currentFormat24h'), children: timeFormat === '12h' ? '12 Hour' : '24 Hour' }), _jsx("div", { className: "arrowDiv btnLeftBorder formatSetterArrowWidth", children: _jsx(ButtonComponent, { imgWidth: 12.5, clickHandler: function () { return toggleTimeFormat(); }, defaultCSS: "arrow left", imgSrc: btnConfig.rightArrow.imgSrc, ariaLabel: timeFormat === '12h' ? 'text.switchTo24h' : 'text.switchTo12h' }) })] })] }) })) }));
};
export default DigitalClock;
